<script setup lang="ts">
import { type ISetBookmarksMediaParams } from "@/types";
import { useBookmarkStore } from "@/stores/bookmark.ts";
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { ThemeButton, ThemeCheckbox, ThemeInput, ThemeModal, ThemeScroller } from "@/ui-components";
import PlaylistAddCheckIcon from "@/assets/icons/playlist-add-check.svg?component";
import PlaylistRemoveIcon from "@/assets/icons/playlist-remove.svg?component";
import UserBookmarksAddToPlaylistCheckbox from "./UserBookmarksAddToPlaylistCheckbox.vue";

import { Form, Field, defineRule } from "vee-validate";
import MerklisteIcon from "@/assets/icons/merkliste.svg?component";
import PlusIcon from "@/assets/icons/plus.svg?component";

const bookmarkStore = useBookmarkStore();

const {
  getBookmarksContainsMedia,
  getDefaultBookmarkName,
  addMediaToBookmarks,
  removeMediaFromBookmarks,
} = bookmarkStore;
const { bookmarksContainsMediaId, bookmarksStats } = storeToRefs(bookmarkStore);
const selectedBookmarks = ref<string[]>([]);

const form = ref<{ validate: () => Promise<{ valid: boolean; errors: Record<string, string> }> }>();
const visibility = ref(false);
const createAvailable = ref(false);
const newBookmarkName = ref("");
const bookmarkNameAlreadyExist = ref();

const emit = defineEmits(["update:modelValue", "confirm"]);

const updateVisibility = (value: boolean) => {
  if (!value) {
    visibility.value = false;
  } else {
    visibility.value = true;
  }
};
const show = async (id: string) => {
  await getBookmarksContainsMedia(id);
  visibility.value = true;
};

watch(visibility, () => {
  selectedBookmarks.value = [];
});

const title = "Medium merken";
const description = "Wohin soll dieses Medium gespeichert werden?";
const confirmTilte = computed(() => {
  return createAvailable.value ? "Erstellen & hinzufügen" : "Hinzufügen";
});

const filteredBookmarks = computed(() => {
  return (bookmarksStats.value ?? []).slice().sort((a, b) => {
    if (a.containsMedia !== b.containsMedia) {
      return a.containsMedia ? -1 : 1;
    }
    if (a.userDefault) {
      return -1;
    }
    if (b.userDefault) {
      return 1;
    }
    return Date.parse(b.createdAt) - Date.parse(a.createdAt);
  });
});

const hide = () => {
  visibility.value = false;
  createAvailable.value = false;
  newBookmarkName.value = "";
};

const action = async () => {
  const params: ISetBookmarksMediaParams = {
    mediaId: bookmarksContainsMediaId.value!,
    bookmarksIds: selectedBookmarks.value,
    newBookmarkName: createAvailable.value
      ? newBookmarkName.value || getDefaultBookmarkName(bookmarksStats.value!)
      : undefined,
  };
  addMediaToBookmarks(params);
  hide();
};

const handleRemoveMedia = async (bookmarkId: string) => {
  await removeMediaFromBookmarks({
    mediaId: bookmarksContainsMediaId.value!,
    bookmarksIds: [bookmarkId],
  });
  await getBookmarksContainsMedia(bookmarksContainsMediaId.value!);
  selectedBookmarks.value = selectedBookmarks.value.filter((id) => id !== bookmarkId);
};

const submit = async () => {
  const valid = await form.value?.validate();
  if (valid) await action();
};

const defaultBookmarkName = computed(() => getDefaultBookmarkName(bookmarksStats.value!)); // TODO: muss das doppelt sein? Vgl. action()

const updateCreateAvailable = async (value: boolean) => {
  if (!value) {
    newBookmarkName.value = "";
  }
};

const showManageMediaHandler = async (event: Event) =>
  await show((event as CustomEvent).detail.mediaId);

onMounted(async () => {
  const { bookmarkNameAlreadyExist: rule } = await import("@/utilities/validationRules.ts");
  defineRule("bookmarkNameAlreadyExist", rule);
  bookmarkNameAlreadyExist.value = rule;

  window.addEventListener("openAddMediaToBookmarksDialog", showManageMediaHandler);
});
onBeforeUnmount(() => {
  window.removeEventListener("openAddMediaToBookmarksDialog", showManageMediaHandler);
});
</script>

<template>
  <ThemeModal :model-value="visibility" @update:model-value="updateVisibility">
    <template #title>{{ title }}</template>
    <template #content>
      <p>{{ description }}</p>
      <Form
        ref="form"
        class="border border-gray-extra-light border-bottom-0 bg-white rounded-top w-100"
        style="min-height: 242px"
      >
        <ThemeScroller
          v-show="filteredBookmarks.length"
          ref="list"
          style="height: 201px"
          :options="{ overflow: { x: 'hidden' }, tabIndex: -1 }"
        >
          <label
            v-for="bookmark in filteredBookmarks"
            :key="bookmark.id"
            class="mb-0 option h-5 d-flex align-items-center px-2 text-normal text-gray-dark"
            :for="`selected-${bookmark.id}`"
            :class="{ 'text-black': selectedBookmarks.includes(bookmark.id) }"
          >
            <MerklisteIcon class="flex-shrink-0 me-1" width="16" style="margin-bottom: 2px" />
            <div
              class="line-height-lg w-100 me-auto text-black text-nowrap overflow-hidden text-truncate"
              :aria-label="bookmark.name + ' Merkliste'"
            >
              {{ bookmark.name }}
            </div>
            <span
              class="flex-shrink-0 me-2 text-right small"
              style="min-width: 32px"
              :aria-label="bookmark.mediaCount + ' Medien in der Merkliste'"
            >
              {{ bookmark.mediaCount }}
            </span>
            <UserBookmarksAddToPlaylistCheckbox
              v-if="!bookmark.containsMedia"
              v-model="selectedBookmarks"
              class="me-2"
              :value="bookmark.id"
              :aria-label="'Medium zu Merkliste ' + bookmark.name + ' hinzufügen'"
            />
            <div
              v-else
              class="flex-shrink-0 me-2 cursor-pointer playlist-icon-wrapper"
              :aria-label="'Medium aus der Merkliste ' + bookmark.name + ' entfernen'"
              @click.prevent="handleRemoveMedia(bookmark.id)"
            >
              <PlaylistAddCheckIcon class="playlist-add-check-icon" width="16" />
              <PlaylistRemoveIcon class="playlist-remove-icon" width="16" />
            </div>
          </label>
        </ThemeScroller>
        <Field
          v-if="bookmarkNameAlreadyExist"
          v-slot="{ errors, componentField, handleChange }"
          v-model="newBookmarkName"
          name="bookmarkName"
          rules="bookmarkNameAlreadyExist"
        >
          <ThemeInput
            v-model="componentField['modelValue']"
            :input-class="`border px-5 pr-6 rounded-down border-gray-extra-light create-input ${errors[0] ? 'error' : null}`"
            @click="createAvailable = true"
            @keydown.enter.prevent="createAvailable = true"
            @update:model-value="handleChange"
          >
            <template #prepend>
              <div
                class="h-5 pr-2 position-absolute d-flex pointer-events-none align-items-center w-100"
              >
                <component
                  :is="createAvailable || newBookmarkName ? MerklisteIcon : PlusIcon"
                  class="ms-2"
                  width="16"
                />
                <div v-if="!createAvailable && !newBookmarkName" class="ms-1 bg-white text-black">
                  &#8203;Neue Liste erstellen
                </div>
                <div
                  v-if="createAvailable && !newBookmarkName"
                  class="ms-1 text-gray-light text-nowrap line-clamp"
                >
                  &#8203;&nbsp;{{ defaultBookmarkName }}
                </div>
              </div>
            </template>
            <template #append>
              <div
                class="h-5 pr-2 position-absolute d-flex align-items-center"
                style="right: 0; top: 0"
              >
                <ThemeCheckbox
                  v-if="createAvailable"
                  v-model="createAvailable"
                  class="ms-auto me-n1"
                  variant="light"
                  aria-label="Neue Merkliste erstellen"
                  @update:model-value="updateCreateAvailable"
                />
              </div>
            </template>
          </ThemeInput>
          <div class="text-error small">{{ errors[0] }}</div>
        </Field>
      </Form>
    </template>
    <template #footer>
      <div class="w-100">
        <ThemeButton class="me-2" variant="light" @click.prevent="hide">Abbrechen</ThemeButton>
        <ThemeButton
          variant="primary"
          :disabled="!selectedBookmarks.length && !createAvailable"
          @click.prevent="submit"
        >
          {{ confirmTilte }}
        </ThemeButton>
      </div>
    </template>
  </ThemeModal>
</template>

<style lang="scss" scoped>
.option {
  &:hover {
    background: var(--primary-extra-light);
  }
}

.playlist-icon-wrapper {
  position: relative;

  svg {
    width: 24px;
    height: 24px;
  }

  .playlist-add-check-icon {
    fill: var(--palette-4);
    opacity: 1;
    transition: opacity 0.2s;
  }

  .playlist-remove-icon {
    fill: var(--error);
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    transition: opacity 0.2s;
  }

  &:hover {
    .playlist-add-check-icon {
      opacity: 0;
    }

    .playlist-remove-icon {
      opacity: 1;
    }
  }
}

:deep(.create-input) {
  &:hover {
    box-shadow: inset 0 0 0 2px var(--primary);
  }
  &.error {
    box-shadow: inset 0 0 0 2px var(--error);
  }
  &:focus:not(.error) {
    border: 0;
    box-shadow: inset 0 0 0 2px var(--primary);
  }
}
</style>
